import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { validateApiResponse } from '../../../services';
import { addRoleMembers, createRole, deleteRole, removeRoleMember, updateRole } from '../../../services/rolesService';
import { mapApiRoleToUi } from './useRoles';

const NAME_TAKEN_STATUS = 409;

/**
 * Role mutations: `update(id, patch, { successKey })`, `create(payload)`, `remove(role)`,
 * `assignMember(role, member)` and `unassignMember(role, member)`. `update` and `create` resolve
 * `{ ok, role?, status? }` (the mapped server role on success); the others resolve a boolean. Each
 * toasts its outcome, except `create` and a rename (`update` with a `name`) failing with 409, a
 * duplicate name, which the caller shows inline.
 */
export const useRoleMutations = () => {
  const { t } = useTranslation('people-access');
  const toast = useToast();
  const toastRef = useRef(toast);
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const fail = useCallback(
    (titleKey, err, { inlineConflict = false } = {}) => {
      if (inlineConflict && err?.response?.status === NAME_TAKEN_STATUS) return;
      toastRef.current.error({ title: t(titleKey), message: err?.message ?? t(titleKey) });
    },
    [t],
  );

  const update = useCallback(
    async (id, patch, { successKey = 'roles.detail.toast.updated' } = {}) => {
      try {
        const { body } = validateApiResponse(await updateRole(id, patch), t('roles.detail.toast.failed'));
        const role = mapApiRoleToUi(body);
        toastRef.current.success({
          title: t(`${successKey}.title`),
          message: t(`${successKey}.message`, { name: role.name }),
        });
        return { ok: true, role };
      } catch (err) {
        fail('roles.detail.toast.failed', err, { inlineConflict: 'name' in patch });
        return { ok: false, status: err?.response?.status };
      }
    },
    [t, fail],
  );

  const create = useCallback(
    async (payload) => {
      try {
        const { body } = validateApiResponse(await createRole(payload), t('roles.builder.toast.failed'));
        const role = mapApiRoleToUi(body);
        toastRef.current.success({
          title: t('roles.builder.toast.created.title'),
          message: t('roles.builder.toast.created.message', { name: role.name }),
        });
        return { ok: true, role };
      } catch (err) {
        fail('roles.builder.toast.failed', err, { inlineConflict: true });
        return { ok: false, status: err?.response?.status };
      }
    },
    [t, fail],
  );

  const remove = useCallback(
    async (role) => {
      try {
        validateApiResponse(await deleteRole(role.id), t('roles.detail.toast.deleteFailed'));
        toastRef.current.success({
          title: t('roles.detail.toast.deleted.title'),
          message: t('roles.detail.toast.deleted.message', { name: role.name }),
        });
        return true;
      } catch (err) {
        fail('roles.detail.toast.deleteFailed', err);
        return false;
      }
    },
    [t, fail],
  );

  const assignMember = useCallback(
    async (role, member) => {
      try {
        validateApiResponse(await addRoleMembers(role.id, [member.id]), t('roles.detail.members.toast.failed'));
        toastRef.current.success({
          title: t('roles.detail.members.toast.assigned.title', { name: member.displayName, role: role.name }),
          message: t('roles.detail.members.toast.assigned.message'),
        });
        return true;
      } catch (err) {
        fail('roles.detail.members.toast.failed', err);
        return false;
      }
    },
    [t, fail],
  );

  const unassignMember = useCallback(
    async (role, member) => {
      try {
        validateApiResponse(await removeRoleMember(role.id, member.id), t('roles.detail.members.toast.failed'));
        toastRef.current.success({
          title: t('roles.detail.members.toast.removed.title', { name: member.displayName, role: role.name }),
          message: t('roles.detail.members.toast.removed.message'),
        });
        return true;
      } catch (err) {
        fail('roles.detail.members.toast.failed', err);
        return false;
      }
    },
    [t, fail],
  );

  return useMemo(
    () => ({ update, create, remove, assignMember, unassignMember }),
    [update, create, remove, assignMember, unassignMember],
  );
};
